import { Color, RGB, RGBA } from "ag-psd";
import * as actionsInterfaces from "@state/auth/types";
import {
  IImageList,
  IState,
} from "@routes/private/Editor/Editor/Pickers/ImagePicker/types";
import { Context } from "react";

export const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/* Set and get query params to URL */

export const getParams = (location: any) => {
  const searchParams = new URLSearchParams(location.search);
  return {
    page: searchParams.get("page") || "",
  };
};

export const setParams = (location: any) => {
  const searchParams = new URLSearchParams();
  searchParams.set("page", location.page || "");
  return searchParams.toString();
};

export const validateNumbers = (e: any, onlyPositive?: boolean) => {
  var theEvent = e || window.event;
  if (theEvent.type === "paste") {
    key = theEvent.clipboardData.getData("text/plain");
  } else {
    var key = theEvent.keyCode || theEvent.which;
    key = String.fromCharCode(key);
  }
  var regex = onlyPositive ? /^[0-9]*$/ : /^-?[0-9]*$/;
  if (!regex.test(key)) {
    theEvent.returnValue = false;
    if (theEvent.preventDefault) theEvent.preventDefault();
  }
};

export const noSpecialCharacters = (e: any) => {
  var theEvent = e || window.event;
  if (theEvent.type === "paste") {
    key = theEvent.clipboardData.getData("text/plain");
  } else {
    var key = theEvent.keyCode || theEvent.which;
    key = String.fromCharCode(key);
  }
  var regex = /^[a-zA-ZÁáÉéÍíÓóÚúÑñığüşöçİĞÜŞÖÇ0-9 ]*$/;
  if (
    !regex.test(key) &&
    theEvent.key !== "Enter" &&
    theEvent.key !== "Tab" &&
    theEvent.key !== "Backspace" &&
    theEvent.key !== "ArrowUp" &&
    theEvent.key !== "ArrowDown" &&
    theEvent.key !== "ArrowLeft" &&
    theEvent.key !== "ArrowRight"
  ) {
    theEvent.returnValue = false;
    if (theEvent.preventDefault) theEvent.preventDefault();
  }
};

export const capitalizeFirstLetter = (text?: string) => {
  if (!text) return "";
  if (text[0]) text = text[0]?.toUpperCase() + text.substring(1)?.toLowerCase();
  return text;
};

export const componentToDec = (c: string) => {
  return parseInt(c, 16);
};

export const hexToRgba = (
  color: string,
  getA?: boolean
): RGB | RGBA | undefined => {
  if (color.length > 6) {
    let rgbColor: RGB & { a?: number } = {
      r: componentToDec(color[1] + color[2]),
      g: componentToDec(color[3] + color[4]),
      b: componentToDec(color[5] + color[6]),
    };
    if (color.length === 9 && getA)
      rgbColor.a = componentToDec(color[7] + color[8]);
    return rgbColor;
  } else {
    return undefined;
  }
};

export const componentToHex = (c: number) => {
  var hex = Math.ceil(c).toString(16);
  return hex.length == 1 ? "0" + hex : hex;
};

export const rgbaToHex = (color: Color | undefined, getA?: boolean) => {
  const rgbaColor = color as RGBA;
  if (
    color &&
    rgbaColor.r !== undefined &&
    rgbaColor.g !== undefined &&
    rgbaColor.b !== undefined
  ) {
    const newA: string =
      rgbaColor.a !== undefined && getA ? componentToHex(rgbaColor.a) : "";
    return (
      "#" +
      componentToHex(rgbaColor.r) +
      componentToHex(rgbaColor.g) +
      componentToHex(rgbaColor.b) +
      newA
    );
  } else {
    return undefined;
  }
};

export const getMidColor = (colorA: RGB, colorB: RGB, midpoint: number) => {
  if (
    Object.values(colorA).every((c) => c >= 0) &&
    Object.values(colorB).every((c) => c >= 0)
  ) {
    return {
      r: getAverageValue(colorA.r, colorB.r, midpoint),
      g: getAverageValue(colorA.g, colorB.g, midpoint),
      b: getAverageValue(colorA.b, colorB.b, midpoint),
    };
  }
  return { r: 255, g: 255, b: 255 };
};

export const getAverageValue = (
  value1: number,
  value2: number,
  midpoint: number
) => {
  return value1 * (1 - midpoint) + value2 * midpoint;
};

export const getValueFromLinearFunction = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x: number
) => {
  if (x2 - x1 === 0) return x === x2 ? y2 : y1;
  const m = (y2 - y1) / (x2 - x1);
  return m * (x - x1) + y1;
};

export const getWidthHeightFromSrc = (src: string) => {
  return new Promise<{ width: number; height: number } | undefined>(
    (resolve, reject) => {
      const image = new Image();
      image.src = src;
      image.onload = () => {
        const dimensions = { width: image.width, height: image.height };
        resolve(dimensions);
      };
      image.onerror = () => {
        reject(undefined);
      };
    }
  );
};

export const loadImageAsync = (src: string, from?: string) => {
  return new Promise<HTMLImageElement | undefined>((resolve, reject) => {
    let img = new Image();
    if (from) img.crossOrigin = from;
    img.onload = () => resolve(img);
    img.onerror = () => reject(null);
    img.src = src;
  });
};

export const getArrayBuffer = (file: File) => {
  return new Promise<ArrayBuffer | undefined>((resolve, reject) => {
    var reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = function () {
      const result = reader.result as ArrayBuffer;
      if (reader.result) resolve(result);
      else resolve(undefined);
    };
    reader.onerror = function (error) {
      console.error(error);
      reject(error);
    };
  });
};

export const updateRole = (
  action: actionsInterfaces.ISignInAction,
  dsPermissions: string[]
) => {
  if (
    action?.user?.profile?.role == actionsInterfaces.IUserType.DEALER_USER &&
    !dsPermissions.find((permission) => permission === "DESIGNS_ACCESS")
  ) {
    action.user.profile.role =
      actionsInterfaces.IUserType.NOTALLOWED_DEALER_USER;
  }
  return action;
};

export const getBase64 = (name: string, file: File, userFile: IImageList) => {
  return new Promise((resolve, reject) => {
    var reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function () {
      const result = reader.result?.toString();
      if (result) {
        //updateImageList(imageList, name, 'uploading', {...userFile, src: result});
        //sendFileResponse(name, result);
        resolve(result);
      }
    };
    reader.onerror = function (error) {
      console.error(error);
      reject(error);
    };
  });
};

export const validateFiles = (
  files: File[],
  imageFileTypes: string[],
  setFilesUploaded: (filesUploaded: IState["files"]) => void,
  context: any
) => {
  let errorFiles: string[] = [];
  let newFiles: File[] = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    //DOS-122: Validate image file extensions to exclude *.jfif file that also have  = MIME type image/jpeg
    let fileExt = (file.name.split(".").pop() || "").toLowerCase();
    //if file has jpg or svg as extension then use its MIME type = image/jpeg or image/svg+xml)
    let customFileType =
      fileExt === "jpg" || fileExt === "svg" ? file.type : `image/${fileExt}`;
    if (
      imageFileTypes.includes(file.type) &&
      imageFileTypes.includes(customFileType)
    )
      newFiles = [...newFiles, file];
    else errorFiles = [...errorFiles, file.name];
  }
  if (errorFiles.length > 0) {
    context.onWarningScreenChange(
      true,
      "Upload File Error",
      `The available image types are: jpeg, png and svg.`,
      "",
      undefined,
      "OKAY",
      "Invalid File Type",
      errorFiles
    );
  }
  setFilesUploaded(newFiles);
};
